import React from "react";
import { useLocation } from "react-router-dom";
import { NavBar } from "../NavBar";
import { PageWrapperProps } from "./PageWrapper.types";
import { ROUTES } from "../../../types/routes";
// 直接具体路径，不走 hooks / utils barrel，否则会把整套 hooks（useQueuedPopup 等）
// 拖进 Base → PageWrapper 引入链，产生跨模块巨型循环依赖
import { usePageConfig, resolvePageConfig } from "../../../hooks/usePageConfig";
import "./PageWrapper.scss";
import { BackIcon } from "../../Icon/IconSvgMap";
import { useNavigation } from "../../../utils/navigation";
import { useT } from "../../../i18n/hooks";

export const PageWrapper: React.FC<PageWrapperProps> = ({
  children,
  title,
  showNavBar = true,
  rightComponent,
  className = "",
  testId,
}) => {
  const { goBack } = useNavigation();
  const pageConfig = usePageConfig();
  const location = useLocation();
  const t = useT();

  // 判断是否为首页
  const isHomePage =
    location.pathname === ROUTES.HOME || location.pathname === ROUTES.HOME_ALT;

  // 使用外层 subpage header 的页面（不需要内部 NavBar）
  const usesSubpageHeader =
    location.pathname === ROUTES.PROFILE ||
    location.pathname === ROUTES.EVENT_CENTER ||
    location.pathname === ROUTES.INVITE_FRIENDS ||
    location.pathname === ROUTES.AGENT_CENTER ||
    location.pathname === ROUTES.VIP ||
    location.pathname === ROUTES.RECORDS_DEPOSIT ||
    location.pathname === ROUTES.REDEEM ||
    location.pathname === ROUTES.MESSAGE_CENTER ||
    location.pathname === ROUTES.ACCOUNT_DETAILS;

  // 如果不需要显示NavBar、是首页、或使用外层 subpage header，直接返回children
  if (!showNavBar || isHomePage || usesSubpageHeader) {
    return <>{children}</>;
  }

  // 根据路由自动设置标题（救援金规则说明页从 location.state 取 ruleTitle，显示在顶部）
  const getPageTitle = () => {
    if (title) return title;
    if (location.pathname === ROUTES.RESCUE_FUND_RULE) {
      const state = location.state as { ruleTitle?: string } | undefined;
      if (state?.ruleTitle) return state.ruleTitle;
    }
    return (
      resolvePageConfig(location.pathname, pageConfig)?.title ||
      t("navigation.page")
    );
  };
  const getPageIcon = () => {
    if (rightComponent) return rightComponent;
    return resolvePageConfig(location.pathname, pageConfig)?.icon;
  };

  const handleBack = () => {
    goBack();
  };

  // 渲染右侧组件
  const renderRightComponent = () => {
    if (getPageIcon()) {
      return getPageIcon();
    }
    return <></>;
  };

  const isInviteFriendsPage = location.pathname === ROUTES.INVITE_FRIENDS;
  const isEventCenterPage = location.pathname === ROUTES.EVENT_CENTER;
  const isRewardsPage = location.pathname === ROUTES.VIP;
  // 与底部 Tab 对应的根页：刷新后无 navigation state，仍按路径隐藏返回
  const hideBackButtonForTabRoot =
    isInviteFriendsPage || isEventCenterPage || isRewardsPage;

  return (
    <div
      className={`page-wrapper ${className}${
        isInviteFriendsPage ? " page-wrapper--invite-friends" : ""
      }${isEventCenterPage ? " page-wrapper--event-center" : ""}`}
      data-testid={testId}
    >
      <NavBar
        title={getPageTitle()}
        back={hideBackButtonForTabRoot ? null : true}
        backIcon={hideBackButtonForTabRoot ? false : <BackIcon />}
        onBack={hideBackButtonForTabRoot ? undefined : handleBack}
        right={renderRightComponent()}
        className="page-wrapper__navbar"
        variant="gradient"
      />
      <div className="page-wrapper__content">{children}</div>
    </div>
  );
};

export default PageWrapper;
